/** No credentials, identifiers, response bodies or URLs are logged by the transport. */
export class SessionEpoch {
  #value = 0;
  capture() { return this.#value; }
  invalidate() { this.#value++; }
  isCurrent(value) { return value === this.#value; }
}

export function isJsonContentType(contentType) {
  const mediaType = contentType?.split(';', 1)[0]?.trim().toLowerCase();
  return mediaType === 'application/json' || mediaType?.endsWith('+json') === true;
}

export async function readJson(response, requireContentType = false) {
  if (requireContentType && !isJsonContentType(response.headers.get('content-type'))) return null;
  try { return await response.json(); } catch { return null; }
}

export async function readErrorData(response) {
  const value = await readJson(response, true);
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

/** At most one opt-in GET retry. Mutations and aborts are never retried. */
export async function fetchWithPolicy(input, init = {}, policy = {}) {
  const retry = policy.retryGet === true && (init.method ?? 'GET').toUpperCase() === 'GET';
  const wait = policy.wait ?? (ms => new Promise(resolve => setTimeout(resolve, ms)));
  const assertCurrent = () => {
    init.signal?.throwIfAborted();
    policy.assertCurrent?.();
  };
  for (let attempt = 0; ; attempt++) {
    assertCurrent();
    let response;
    try { response = await fetch(input, init); }
    catch (error) {
      assertCurrent();
      if (!retry || attempt !== 0 || error?.name === 'AbortError') throw error;
    }
    assertCurrent();
    if (response && (!retry || attempt !== 0 || ![502, 503, 504].includes(response.status))) return response;
    // Release an unused response before retrying. Do not consume/log patient data.
    await response?.body?.cancel().catch(() => {});
    await wait(policy.delayMs ?? 1000);
  }
}
